import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useOperation, useActiveOperations } from '@/hooks/useOperation';
import { gql, useMutation, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  IconButton,
  Stack,
  Divider,
  Avatar,
  Tooltip,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Tag as TagIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AttachMoney as RevenueIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Category as CategoryIcon,
  MoreVert as MoreIcon,
  DriveFileMove as MoveIcon,
  Label as LabelIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Computer as ComputerIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ContactPhone as ContactIcon,
  SmartToy as SmartToyIcon,
  CallMerge as MergeIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { Gauge } from '@mui/x-charts/Gauge';
import { decodeHtmlEntities } from '../../../../utils/htmlEntityDecoder';

import { FLAG_URL } from '../../../../contants';
import CompanyLinks from '../../../../components/CompanyLinks/CompanyLinks';
import CompanyAvatar from '../../../../components/CompanyAvatar';
import EditButton from '../EditButton/EditButton';
import { ValidationSteps } from '../../../ValidationPage/types';
import {
  useEditCompanyMutation,
  useResetDealAssessmentUploadMutation,
  useValidateCompanyWithRagMutation,
  useGetRagJobStatusLazyQuery,
  useGetActiveRagValidationsLazyQuery,
  useCompanyContactsQuery,
  useEnrichContactMutation,
  useGetEnrichmentJobStatusLazyQuery,
} from '../../../../gql/generated/graphql';
import KPIStatusModal from '../../../../components/KPIStatusModal/KPIStatusModal';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import MoveCompanyDialog from '../../../../components/MoveCompanyDialog/MoveCompanyDialog';
import { format } from 'date-fns';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useFinancialDataAvailability } from '../../../../hooks/useFinancialDataAvailability';
import FinancialDataModal from '../../../../components/FinancialDataModal/FinancialDataModal';
import PnlForecastPopover from './PnlForecastPopover';
import TrendSparkline, { computeTrendInfo } from './TrendSparkline';

const REFRESH_COMPANY_GOOGLE_DRIVE_FILES = gql`
  mutation RefreshCompanyGoogleDriveFiles($companyId: Int!) {
    refreshCompanyGoogleDriveFiles(companyId: $companyId) {
      success
      message
    }
  }
`;

type RefreshCompanyGoogleDriveFilesResponse = {
  refreshCompanyGoogleDriveFiles?: {
    success: boolean;
    message?: string | null;
  } | null;
};

type RefreshCompanyGoogleDriveFilesVariables = {
  companyId: number;
};

interface CompanyOverviewProps {
  company: {
    dbId?: number;
    shortName?: string;
    legalName?: string;
    uniqueDomain?: string;
    country?: string;
    street?: string;
    zipCode?: string;
    city?: string;
    linkedinDescription?: string;
    linkedinUrl?: string;
    pipedriveId?: string;
    validated?: boolean;
    isRelevant?: boolean;
    iabDomainCategory?: string;
    managingDirector?: string;
    contactEmail?: string;
    contactEmailType?: string;
    contactEnrichmentLog?: Array<{
      timestamp?: string;
      extractionMethod?: string | null;
      contactEmailExtracted?: boolean | null;
      contactEmailSource?: string | null;
      managingDirectorExtracted?: boolean | null;
      managingDirectorSource?: string | null;
      hunterCalled?: boolean | null;
      firecrawlSuccess?: boolean | null;
      errorMessage?: string | null;
      processingTimeSeconds?: number | null;
    }> | null;
  };
  companyAnalyses?: {
    fte?: number;
    revenue?: number;
  };
  callabilityScore?: number;
  ebit?: number;
  absSales?: number;
  salesGrowth?: number;
  kpiStatusData?: {
    kpiCalculationStatus?: {
      lastKpiCalculation?: string | null;
      lastDataUpload?: string | null;
      lastFiscalYear?: number | null;
    } | null;
  };
  onEdit?: (step: ValidationSteps) => void;
  productCategories?: Array<{
    dbId?: number;
    subCategory1?: string;
    subCategory2?: string | null;
    subCategory3?: string | null;
  }>;
  categoryTrends?: Array<{
    softwareCategoryId?: number | null;
    subCategory1?: string | null;
    date?: string | null;
    interest?: number | null;
  } | null> | null;
  keywords?: Array<{
    keyword: string;
    cpc?: number | null;
    searchVolume?: number | null;
    competitionIndex?: number | null;
    keywordType?: string | null;
  }>;
  isLegacyKeywords?: boolean;
  onDownloadAssessment?: () => void;
  isGeneratingFile?: boolean;
  generationTime?: string;
  hasDataUploaded?: boolean;
  isGenerationStuck?: boolean;
  progress?: {
    step?: string | null;
    timestamp?: string | null;
    secondsSinceLastUpdate?: number | null;
  } | null;
  onMenuOpen?: (event: React.MouseEvent<HTMLElement>) => void;
  anchorEl?: HTMLElement | null;
  onMenuClose?: () => void;
  onMergeDialogOpen?: () => void;
}

// Format enrichment log entries into human-readable tooltip lines
const formatEnrichmentLog = (log: CompanyOverviewProps['company']['contactEnrichmentLog']): string[] => {
  if (!log || log.length === 0) return ['No enrichment attempts recorded'];
  return log.map(entry => {
    const date = entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : '?';
    const time = entry.processingTimeSeconds ? `${entry.processingTimeSeconds.toFixed(1)}s` : '';

    // Build a concise result description
    const parts: string[] = [];
    if (entry.contactEmailExtracted) {
      parts.push(`Email found via ${entry.contactEmailSource || 'unknown'}`);
    }
    if (entry.managingDirectorExtracted) {
      parts.push(`MD found via ${entry.managingDirectorSource || 'unknown'}`);
    }
    if (entry.errorMessage) {
      parts.push(entry.errorMessage);
    }
    if (parts.length === 0) {
      if (entry.extractionMethod) {
        parts.push(`Method: ${entry.extractionMethod}, no results`);
      } else {
        parts.push('No results');
      }
    }

    // Sources tried
    const sources: string[] = [];
    if (entry.hunterCalled) sources.push('Hunter');
    if (entry.firecrawlSuccess != null) sources.push('Firecrawl');
    if (entry.extractionMethod === 'full' || entry.extractionMethod === 'email_only') sources.push('OpenAI');

    const sourceLine = sources.length > 0 ? ` [${sources.join(', ')}]` : '';
    return `${date}: ${parts.join('; ')}${sourceLine}${time ? ` (${time})` : ''}`;
  });
};

// Helper function to convert progress step to user-friendly message
const getProgressMessage = (step: string | null | undefined): string => {
  if (!step) return 'Processing';

  const messages: Record<string, string> = {
    validating_company_data: 'Validating company data',
    fetching_benchmark_data: 'Fetching benchmark data',
    creating_excel_file: 'Creating Excel file',
    uploading_to_s3: 'Uploading to S3',
    updating_database: 'Finalizing',
    completed: 'Completed',
  };

  return messages[step] || 'Processing';
};

const CompanyOverview: React.FC<CompanyOverviewProps> = ({
  company,
  companyAnalyses,
  callabilityScore,
  ebit,
  absSales,
  salesGrowth,
  kpiStatusData,
  onEdit,
  productCategories = [],
  categoryTrends,
  keywords = [],
  isLegacyKeywords = false,
  onDownloadAssessment,
  isGeneratingFile = false,
  generationTime,
  hasDataUploaded = true,
  isGenerationStuck = false,
  progress,
  onMenuOpen,
  anchorEl,
  onMenuClose,
  onMergeDialogOpen,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const apolloClient = useApolloClient();
  const isDark = theme.palette.mode === 'dark';
  const [keywordsExpanded, setKeywordsExpanded] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(decodeHtmlEntities(company?.linkedinDescription) || '');
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [hasRequestedDriveSync, setHasRequestedDriveSync] = useState(false);
  const [imFile, setImFile] = useState<{
    hasFile: boolean;
    fileId?: number;
    fileName?: string;
    driveUrl?: string;
  } | null>(null);
  const [financialDataModalOpen, setFinancialDataModalOpen] = useState(false);

  const trendsByCategory = useMemo(() => {
    const map = new Map<number, { date: string; interest: number }[]>();
    if (!categoryTrends) return map;
    for (const pt of categoryTrends) {
      if (!pt?.softwareCategoryId || pt.date == null || pt.interest == null) continue;
      let arr = map.get(pt.softwareCategoryId);
      if (!arr) {
        arr = [];
        map.set(pt.softwareCategoryId, arr);
      }
      arr.push({ date: pt.date, interest: pt.interest });
    }
    return map;
  }, [categoryTrends]);
  const EXIT_MULTIPLE = 10.3;
  const HOLDING_YEARS = 5;
  const TARGET_MOIC = 2.0;

  const impliedEntryMultiple = useMemo(() => {
    if (ebit == null || ebit <= 0 || salesGrowth == null) return null;
    const exitEbitMultiplier = Math.pow(1 + salesGrowth, HOLDING_YEARS);
    return (EXIT_MULTIPLE * exitEbitMultiplier) / TARGET_MOIC;
  }, [ebit, salesGrowth]);

  const [contactsExpanded, setContactsExpanded] = useState(false);

  const [editCompanyMutation, { loading: editLoading }] = useEditCompanyMutation();
  const [refreshCompanyDrive] = useMutation<
    RefreshCompanyGoogleDriveFilesResponse,
    RefreshCompanyGoogleDriveFilesVariables
  >(REFRESH_COMPANY_GOOGLE_DRIVE_FILES);
  const [resetDealAssessmentUpload, { loading: resetLoading }] = useResetDealAssessmentUploadMutation();

  // RAG Validation state and hooks
  const [validateCompanyWithRag] = useValidateCompanyWithRagMutation();
  const [getRagJobStatus] = useGetRagJobStatusLazyQuery();
  const [getActiveRagValidations] = useGetActiveRagValidationsLazyQuery();
  const [ragValidationJob, setRagValidationJob] = useState<{
    jobId: string;
    progress: number;
    status: string;
    message?: string;
  } | null>(null);
  const ragPollingInterval = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedActiveValidation = useRef(false);

  // Contact Enrichment state and hooks
  const [enrichContact] = useEnrichContactMutation();
  const [getEnrichmentJobStatus] = useGetEnrichmentJobStatusLazyQuery();
  const [contactEnrichmentJob, setContactEnrichmentJob] = useState<{
    jobId: string;
    progress: number;
    status: string;
    message?: string;
  } | null>(null);
  const contactEnrichmentPollingInterval = useRef<NodeJS.Timeout | null>(null);
  const [contactMenuAnchorEl, setContactMenuAnchorEl] = useState<HTMLElement | null>(null);

  // New: operation-based enrichment tracking (SSE-based, replaces polling when operationId is available)
  const [enrichmentOperationId, setEnrichmentOperationId] = useState<string | null>(null);
  const { state: enrichmentOp, isComplete: enrichmentComplete } = useOperation(enrichmentOperationId);
  const activeOps = useActiveOperations('company', company?.dbId ?? null);

  // Auto-attach to active enrichment operation on mount (survives page navigation)
  useEffect(() => {
    const activeEnrichment = activeOps.find((op: any) => op.type === 'contact_enrichment');
    if (activeEnrichment && !enrichmentOperationId) {
      setEnrichmentOperationId(activeEnrichment.operationId);
    }
  }, [activeOps, enrichmentOperationId]);

  // Refetch company data when enrichment completes (replaces window.location.reload)
  useEffect(() => {
    if (enrichmentComplete) {
      setEnrichmentOperationId(null);
      apolloClient.refetchQueries({ include: ['getCompanyForDFM'] });
    }
  }, [enrichmentComplete, apolloClient]);

  const companyIdNumber = company?.dbId
    ? typeof company.dbId === 'string'
      ? parseInt(company.dbId, 10)
      : company.dbId
    : 0;

  const lastFiscalYear = kpiStatusData?.kpiCalculationStatus?.lastFiscalYear;

  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
  } = useCompanyContactsQuery({
    variables: { companyId: companyIdNumber },
    skip: !company?.dbId,
  });

  // Check financial data availability
  const {
    hasFinancialData,
    hasPLData,
    hasBalanceSheetData,
    loading: financialDataLoading,
    error: financialDataError,
  } = useFinancialDataAvailability(companyIdNumber);

  // Generate tooltip message for Financial Data button
  const getFinancialDataTooltip = () => {
    if (financialDataLoading) {
      return 'Loading financial data availability...';
    }

    if (financialDataError) {
      return 'Error checking financial data availability';
    }

    if (!hasFinancialData) {
      return 'No financial data available for this company';
    }

    if (hasFinancialData) {
      const availableData = [];
      if (hasPLData) availableData.push('P&L');
      if (hasBalanceSheetData) availableData.push('Balance Sheet');
      return `View financial data: ${availableData.join(' and ')}`;
    }

    return 'Financial data';
  };

  useEffect(() => {
    setHasRequestedDriveSync(false);
    hasCheckedActiveValidation.current = false;
    // Clear any existing RAG validation state when company changes
    setRagValidationJob(null);
    if (ragPollingInterval.current) {
      clearInterval(ragPollingInterval.current);
      ragPollingInterval.current = null;
    }
    // Clear any existing contact enrichment state when company changes
    setContactEnrichmentJob(null);
    if (contactEnrichmentPollingInterval.current) {
      clearInterval(contactEnrichmentPollingInterval.current);
      contactEnrichmentPollingInterval.current = null;
    }
  }, [companyIdNumber]);

  useEffect(() => {
    if (!companyIdNumber || hasRequestedDriveSync) {
      return;
    }

    setHasRequestedDriveSync(true);

    refreshCompanyDrive({ variables: { companyId: companyIdNumber } })
      .then(result => {
        const message = result.data?.refreshCompanyGoogleDriveFiles?.message;
        if (message) {
          console.debug('refreshCompanyGoogleDriveFiles:', message);
        }
      })
      .catch(error => {
        console.error('Failed to refresh Google Drive files:', error);
        setHasRequestedDriveSync(false);
      });
  }, [companyIdNumber, hasRequestedDriveSync, refreshCompanyDrive]);

  // Fetch IM file data
  useEffect(() => {
    if (!companyIdNumber) {
      setImFile(null);
      return;
    }

    const backendUrl = process.env.REACT_APP_FILESERVER_BACKEND || 'http://localhost:5001/files';
    fetch(`${backendUrl}/company/${companyIdNumber}/im_file`)
      .then(response => response.json())
      .then(data => {
        setImFile(data);
      })
      .catch(error => {
        console.error('Failed to fetch IM file:', error);
        setImFile({ hasFile: false });
      });
  }, [companyIdNumber]);

  const companyName =
    (company?.shortName !== '<unnamed reference customer>' && company?.shortName) ||
    company?.legalName ||
    company?.uniqueDomain;

  const formatRevenue = (revenue?: number): string => {
    if (!revenue) return 'N/A';
    // Backend numbers are already in thousands
    if (revenue >= 1000) {
      return `€${(revenue / 1000).toFixed(1)}M`;
    }
    if (revenue >= 1) {
      return `€${revenue.toFixed(0)}K`;
    }
    return `€${(revenue * 1000).toLocaleString()}`;
  };

  const formatFTE = (fte?: number): string => {
    if (!fte) return 'N/A';
    return fte.toLocaleString();
  };

  const formatEBIT = (ebit?: number): string => {
    if (ebit === undefined || ebit === null) return 'N/A';
    const absValue = Math.abs(ebit);
    let formatted: string;
    // Backend numbers are already in thousands
    if (absValue >= 1000) {
      formatted = `€${(absValue / 1000).toFixed(1)}M`;
    } else if (absValue >= 1) {
      formatted = `€${absValue.toFixed(0)}K`;
    } else {
      formatted = `€${(absValue * 1000).toLocaleString()}`;
    }
    return ebit < 0 ? `-${formatted}` : formatted;
  };

  const handleEditDescription = () => {
    setIsEditingDescription(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setDescriptionValue(decodeHtmlEntities(company?.linkedinDescription) || '');
  };

  const handleSaveDescription = async () => {
    if (!company?.dbId) return;

    try {
      await editCompanyMutation({
        variables: {
          companyId: company.dbId,
          companyDescription: descriptionValue,
        },
        refetchQueries: ['getCompanyForDFM'],
      });
      setIsEditingDescription(false);
      // Note: The UI will update when the query refetches with the new data
    } catch (error) {
      console.error('Error updating company description:', error);
      // Keep the edit mode open on error so user can try again
    }
  };

  const handleResetDealAssessment = async () => {
    if (!company?.dbId) return;

    try {
      const result = await resetDealAssessmentUpload({
        variables: {
          companyId: company.dbId,
        },
      });

      if (result.data?.resetDealAssessmentUpload?.success) {
        showSnackbar('Deal assessment upload reset successfully. You can now upload a new file.', 'success');
        // Refetch company data to update hasDataUploaded flag
        window.location.reload();
      } else {
        showSnackbar(
          result.data?.resetDealAssessmentUpload?.message || 'Failed to reset deal assessment upload',
          'error'
        );
      }
    } catch (error) {
      console.error('Error resetting deal assessment upload:', error);
      showSnackbar('An error occurred while resetting deal assessment upload', 'error');
    }
  };

  const handleOpenIMFile = () => {
    if (imFile?.driveUrl) {
      window.open(imFile.driveUrl, '_blank');
    }
  };

  // Poll RAG job status
  const pollRagJobStatus = useCallback(
    async (jobId: string) => {
      try {
        const result = await getRagJobStatus({
          variables: { jobId },
          fetchPolicy: 'network-only',
        });

        const status = result.data?.ragJobStatus;
        if (!status) return;

        const progressPercent = status.progress?.percent ?? 0;
        const progressMessage = status.progress?.message || status.progress?.stage || 'Processing';

        setRagValidationJob({
          jobId,
          progress: progressPercent,
          status: status.status || 'pending',
          message: progressMessage,
        });

        // Check if job is complete
        if (status.status === 'complete' || status.status === 'failed') {
          // Clear polling interval
          if (ragPollingInterval.current) {
            clearInterval(ragPollingInterval.current);
            ragPollingInterval.current = null;
          }

          if (status.status === 'complete') {
            showSnackbar('AI validation completed successfully', 'success');
            // Refresh the page to show updated data
            window.location.reload();
          } else if (status.status === 'failed') {
            showSnackbar(`AI validation failed: ${status.error || 'Unknown error'}`, 'error');
          }

          // Clear job state after a short delay
          setTimeout(() => {
            setRagValidationJob(null);
          }, 2000);
        }
      } catch (err) {
        console.error('Error polling RAG job status:', err);
      }
    },
    [getRagJobStatus, showSnackbar]
  );

  // Handle RAG validation trigger
  const handleRagValidation = useCallback(async () => {
    const domain = company?.uniqueDomain;
    if (!domain || ragValidationJob) return;

    // Close the menu
    onMenuClose?.();

    try {
      const result = await validateCompanyWithRag({
        variables: { companyDomain: domain, shadowMode: false },
      });

      const response = result.data?.validateCompanyWithRag;
      const jobId = response?.jobId;

      if (!jobId) {
        showSnackbar(response?.message || 'Failed to start AI validation', 'error');
        return;
      }

      // Set initial job state
      setRagValidationJob({
        jobId,
        progress: 0,
        status: 'pending',
        message: 'Starting validation...',
      });

      showSnackbar('AI validation started', 'info');

      // Start polling for status
      ragPollingInterval.current = setInterval(() => {
        pollRagJobStatus(jobId);
      }, 2000);

      // Initial poll
      pollRagJobStatus(jobId);
    } catch (err) {
      console.error('Error starting RAG validation:', err);
      showSnackbar('Error starting AI validation', 'error');
    }
  }, [company?.uniqueDomain, ragValidationJob, validateCompanyWithRag, pollRagJobStatus, showSnackbar, onMenuClose]);

  // Poll contact enrichment job status
  const pollContactEnrichmentJobStatus = useCallback(
    async (jobId: string) => {
      try {
        const result = await getEnrichmentJobStatus({
          variables: { jobId },
          fetchPolicy: 'network-only',
        });

        const status = result.data?.enrichmentJobStatus;
        if (!status) return;

        const progressPercent = status.progress?.percent ?? 0;
        const progressMessage = status.progress?.message || status.progress?.stage || 'Processing';

        setContactEnrichmentJob({
          jobId,
          progress: progressPercent,
          status: status.status || 'pending',
          message: progressMessage,
        });

        // Check if job is complete
        if (status.status === 'complete' || status.status === 'failed') {
          // Clear polling interval
          if (contactEnrichmentPollingInterval.current) {
            clearInterval(contactEnrichmentPollingInterval.current);
            contactEnrichmentPollingInterval.current = null;
          }

          if (status.status === 'complete') {
            showSnackbar('Contact enrichment completed successfully', 'success', false);
            // Refetch company data to show updated contact info
            apolloClient.refetchQueries({ include: ['getCompanyForDFM'] });
          } else if (status.status === 'failed') {
            showSnackbar(`Contact enrichment failed: ${status.error || 'Unknown error'}`, 'error', false);
          }

          // Clear job state after a short delay
          setTimeout(() => {
            setContactEnrichmentJob(null);
          }, 2000);
        }
      } catch (err) {
        console.error('Error polling contact enrichment job status:', err);
      }
    },
    [getEnrichmentJobStatus, showSnackbar, apolloClient]
  );

  // Handle contact enrichment trigger
  const handleContactEnrichment = useCallback(async () => {
    const companyId = company?.dbId;
    if (!companyId || contactEnrichmentJob) return;

    // Close the menu
    setContactMenuAnchorEl(null);

    try {
      const result = await enrichContact({
        variables: { companyId },
      });

      const response = result.data?.enrichCompanyContact;
      const taskId = response?.taskId;
      const opId = response?.operationId;

      if (!taskId && !opId) {
        showSnackbar(response?.message || 'Failed to start contact enrichment', 'error', false);
        return;
      }

      // If SSE operation tracking is available, activate it (additive)
      if (opId) {
        setEnrichmentOperationId(opId);
      }

      // Always start legacy polling when we have a taskId — ensures feedback
      // even if the SSE facade is not yet deployed
      if (taskId) {
        setContactEnrichmentJob({
          jobId: taskId,
          progress: 0,
          status: 'pending',
          message: 'Starting enrichment...',
        });

        showSnackbar('Contact enrichment started', 'info', false);

        contactEnrichmentPollingInterval.current = setInterval(() => {
          pollContactEnrichmentJobStatus(taskId);
        }, 2000);

        pollContactEnrichmentJobStatus(taskId);
      }
    } catch (err) {
      console.error('Error starting contact enrichment:', err);
      showSnackbar('Error starting contact enrichment', 'error', false);
    }
  }, [company?.dbId, contactEnrichmentJob, enrichContact, pollContactEnrichmentJobStatus, showSnackbar]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      if (ragPollingInterval.current) {
        clearInterval(ragPollingInterval.current);
      }
      if (contactEnrichmentPollingInterval.current) {
        clearInterval(contactEnrichmentPollingInterval.current);
      }
    };
  }, []);

  // Check for active RAG validation on mount (started by other users or on other machines)
  useEffect(() => {
    const checkActiveValidation = async () => {
      const domain = company?.uniqueDomain;
      if (!domain || ragValidationJob || hasCheckedActiveValidation.current) return;

      hasCheckedActiveValidation.current = true;

      try {
        const result = await getActiveRagValidations({
          variables: { companyDomains: [domain] },
          fetchPolicy: 'network-only',
        });

        const activeValidationsJson = result.data?.activeRagValidations;
        if (!activeValidationsJson) return;

        const activeValidations = JSON.parse(activeValidationsJson);
        const validation = activeValidations[domain];

        if (validation && validation.jobId) {
          // Found an active validation, start tracking it
          setRagValidationJob({
            jobId: validation.jobId,
            progress: validation.progress || 0,
            status: validation.status || 'pending',
            message: validation.message || 'Validation in progress...',
          });

          // Start polling for status
          ragPollingInterval.current = setInterval(() => {
            pollRagJobStatus(validation.jobId);
          }, 2000);

          // Initial poll
          pollRagJobStatus(validation.jobId);
        }
      } catch (err) {
        console.error('Error checking active RAG validation:', err);
      }
    };

    checkActiveValidation();
  }, [company?.uniqueDomain, ragValidationJob, getActiveRagValidations, pollRagJobStatus]);

  return (
    <Card
      sx={{
        mb: 0,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          boxShadow: 2,
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Header Row */}
        <Box display="flex" alignItems="flex-start" mb={3}>
          <CompanyAvatar
            companyName={companyName}
            domain={company.uniqueDomain}
            size={64}
            sx={{
              mr: 2,
            }}
          />
          <Box flex={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {companyName}
                </Typography>
                {/* Company Actions Menu */}
                {ragValidationJob && (
                  <Tooltip title={`${ragValidationJob.message} (${ragValidationJob.progress}%)`}>
                    <Box sx={{ display: 'inline-flex', ml: 0.5 }}>
                      <CircularProgress
                        size={24}
                        variant={ragValidationJob.progress > 0 ? 'determinate' : 'indeterminate'}
                        value={ragValidationJob.progress}
                        sx={{ color: '#FF564F' }}
                      />
                    </Box>
                  </Tooltip>
                )}
                <IconButton onClick={onMenuOpen} size="small">
                  <MoreIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onMenuClose}>
                  <MenuItem key="ai-validate" onClick={handleRagValidation} disabled={!!ragValidationJob}>
                    {ragValidationJob ? (
                      <CircularProgress
                        size={18}
                        variant={ragValidationJob.progress > 0 ? 'determinate' : 'indeterminate'}
                        value={ragValidationJob.progress}
                        sx={{ mr: 1, color: '#FF564F' }}
                      />
                    ) : (
                      <SmartToyIcon sx={{ mr: 1, fontSize: 18 }} />
                    )}
                    {ragValidationJob ? `AI Validating (${ragValidationJob.progress}%)` : 'AI Validate'}
                  </MenuItem>
                  <MenuItem key="merge" onClick={onMergeDialogOpen}>
                    <MergeIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t('assessment.dfmMenuMergeItem')}
                  </MenuItem>
                  <MenuItem
                    key="move"
                    onClick={() => {
                      setMoveDialogOpen(true);
                      onMenuClose();
                    }}
                  >
                    <MoveIcon sx={{ mr: 1, fontSize: 18 }} />
                    {t('assessment.dfmMenuMoveItem')}
                  </MenuItem>
                </Menu>
              </Box>

              {/* Product Categories aligned to the right */}
              {productCategories.length > 0 && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {productCategories
                    .filter(cat => cat?.subCategory1)
                    .filter((cat, i, arr) => {
                      const label = cat.subCategory3 || cat.subCategory2 || cat.subCategory1;
                      return arr.findIndex(c => (c.subCategory3 || c.subCategory2 || c.subCategory1) === label) === i;
                    })
                    .slice(0, 3)
                    .map(category => {
                      const trendData = category.dbId ? trendsByCategory.get(category.dbId) : undefined;
                      const trendInfo = trendData ? computeTrendInfo(trendData) : null;
                      const chipLabel = category.subCategory3 || category.subCategory2 || category.subCategory1;
                      const tooltipLines = [category.subCategory1, category.subCategory2, category.subCategory3].filter(
                        Boolean
                      );
                      const tooltipText =
                        tooltipLines.join(' \u203A ') + (trendInfo ? `  \u2022  Google Trends ${trendInfo.label}` : '');
                      return (
                        <Tooltip key={category.dbId} title={tooltipText} arrow>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.25}
                            sx={{ borderRadius: 1, px: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <Chip
                              label={chipLabel}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem', color: '#FF564F', borderColor: '#FF564F' }}
                            />
                            {trendData && (
                              <>
                                <TrendSparkline dataPoints={trendData} />
                                {trendInfo && (
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: '0.6rem', color: trendInfo.color, lineHeight: 1, ml: -0.25 }}
                                  >
                                    {trendInfo.label}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  {productCategories.length > 3 && (
                    <Chip
                      key="more-categories"
                      label={`+${productCategories.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', color: '#FF564F', borderColor: '#FF564F' }}
                    />
                  )}
                  <Tooltip title="Edit product categories">
                    <IconButton
                      size="small"
                      onClick={() => onEdit?.(ValidationSteps.VALIDATE_PRODUCT_CATEGORY)}
                      sx={{ p: 0.3 }}
                    >
                      <MoreIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {company?.validated ? (
                <Chip label="Validated" color="success" size="small" />
              ) : (
                <Chip label="Not Validated" color="warning" size="small" />
              )}
              {company?.isRelevant && (
                <Tooltip title="A user confirmed this company as software via 'Mark as Software' action" arrow>
                  <Chip label="Relevant" color="info" size="small" />
                </Tooltip>
              )}
              {impliedEntryMultiple !== null && (
                <PnlForecastPopover companyId={companyIdNumber}>
                  <Chip
                    label={`2x@5Y: ${impliedEntryMultiple.toFixed(1)}x`}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 'medium',
                      color: 'white',
                      bgcolor: impliedEntryMultiple > 10 ? 'success.main' : '#4E6694',
                    }}
                  />
                </PnlForecastPopover>
              )}

              {/* Callability Score as vertical bar */}
              {callabilityScore != null && callabilityScore > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title={`Callability ${(callabilityScore * 100).toFixed(0)}%`} arrow>
                    <Box
                      sx={{
                        width: 8,
                        height: 32,
                        bgcolor: isDark ? 'grey.800' : 'grey.200',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'help',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: `${callabilityScore * 100}%`,
                          bgcolor:
                            callabilityScore > 0.7
                              ? 'success.main'
                              : callabilityScore > 0.4
                                ? 'warning.main'
                                : 'error.main',
                          transition: 'height 0.3s ease',
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    {(callabilityScore * 100).toFixed(0)}%
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Location and Links Row - above the two-column grid */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
            {company?.country && (
              <img
                src={FLAG_URL(company.country)}
                width={24}
                height={24}
                alt={`${company.country} flag`}
                style={{ borderRadius: 4, flexShrink: 0 }}
              />
            )}
            <Typography color="text.secondary" variant="body2" noWrap>
              {[company?.street, company?.zipCode, company?.city, company?.country].filter(Boolean).join(', ') ||
                'Location not specified'}
            </Typography>
            <Tooltip title="Edit company address">
              <IconButton
                size="small"
                onClick={() => onEdit?.(ValidationSteps.VALIDATE_ADDRESS)}
                sx={{ p: 0.3, flexShrink: 0 }}
              >
                <MoreIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <CompanyLinks
            uniqueDomain={company?.uniqueDomain}
            linkedinUrl={company?.linkedinUrl}
            pipedriveID={company?.pipedriveId}
            direction="row"
            compact
          />
        </Box>

        {/* Company Details Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Metrics */}
          <Grid item xs={12} md={4} order={{ md: 1 }}>
            <Stack spacing={2}>
              {/* Company Size */}
              <Box>
                <Stack spacing={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.5}
                    py={0.75}
                    borderRadius={1}
                    border={1}
                    borderColor={isDark ? 'grey.700' : 'grey.100'}
                    sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Employees (FTE)
                      </Typography>
                      <Tooltip title="Edit company size">
                        <IconButton
                          size="small"
                          onClick={() => onEdit?.(ValidationSteps.VALIDATE_SIZE)}
                          sx={{ p: 0.3 }}
                        >
                          <MoreIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" fontWeight="medium" color="primary.main">
                      {formatFTE(companyAnalyses?.fte)}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.5}
                    py={0.75}
                    borderRadius={1}
                    border={1}
                    borderColor={isDark ? 'grey.700' : 'grey.100'}
                    sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Annual Revenue{lastFiscalYear ? ` (${lastFiscalYear})` : ''}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary.main">
                      {formatRevenue(absSales || companyAnalyses?.revenue)}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1.5}
                    py={0.75}
                    borderRadius={1}
                    border={1}
                    borderColor={isDark ? 'grey.700' : 'grey.100'}
                    sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      EBIT{lastFiscalYear ? ` (${lastFiscalYear})` : ''}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary.main">
                      {formatEBIT(ebit)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          {/* Downloads Section */}
          <Grid item xs={12} md={4} order={{ md: 3 }}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DownloadIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="medium">
                  Downloads
                </Typography>
              </Box>
              <Stack spacing={1}>
                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                  <Button
                    onClick={onDownloadAssessment}
                    disabled={!hasDataUploaded || isGeneratingFile}
                    variant="outlined"
                    size="small"
                    startIcon={
                      isGeneratingFile ? (
                        <CircularProgress size={14} color="primary" />
                      ) : (
                        <AssessmentIcon sx={{ fontSize: 14 }} />
                      )
                    }
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                    }}
                  >
                    {isGeneratingFile ? 'Generating...' : 'Deal Assessment'}
                  </Button>

                  <Tooltip title={getFinancialDataTooltip()} placement="top">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!hasFinancialData || financialDataLoading}
                        onClick={() => setFinancialDataModalOpen(true)}
                        startIcon={
                          financialDataLoading ? (
                            <CircularProgress size={14} color="primary" />
                          ) : (
                            <TrendingUpIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        sx={{
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minWidth: 'auto',
                          opacity: hasFinancialData ? 1 : 0.4,
                        }}
                      >
                        Financial Data
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    size="small"
                    disabled
                    startIcon={<BusinessIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                      opacity: 0.4,
                    }}
                  >
                    Market Analysis
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!imFile?.hasFile}
                    onClick={handleOpenIMFile}
                    startIcon={<FileIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                      opacity: imFile?.hasFile ? 1 : 0.4,
                    }}
                  >
                    IM
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setKpiModalOpen(true)}
                    startIcon={<ShowChartIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                    }}
                  >
                    KPI Status
                    {kpiStatusData?.kpiCalculationStatus?.lastKpiCalculation && (
                      <Typography component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                        ({format(new Date(kpiStatusData.kpiCalculationStatus.lastKpiCalculation), 'dd MMM')})
                      </Typography>
                    )}
                  </Button>
                </Box>

                {isGeneratingFile && !isGenerationStuck && progress && (
                  <Typography variant="caption" color="info.main" display="block">
                    {getProgressMessage(progress.step)}
                    {progress.secondsSinceLastUpdate !== null && progress.secondsSinceLastUpdate !== undefined && (
                      <Typography component="span" sx={{ ml: 0.5 }}>
                        ({Math.round(progress.secondsSinceLastUpdate)}s ago)
                      </Typography>
                    )}
                  </Typography>
                )}

                {isGenerationStuck && (
                  <Box>
                    <Typography variant="caption" color="error.main" display="block" mb={1}>
                      Deal assessment generation appears stuck.
                    </Typography>
                    <Button
                      onClick={handleResetDealAssessment}
                      disabled={resetLoading}
                      variant="text"
                      size="small"
                      startIcon={resetLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0.25,
                        px: 1,
                        minWidth: 'auto',
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: 'error.lighter',
                        },
                      }}
                    >
                      {resetLoading ? 'Resetting...' : 'Reset & Retry'}
                    </Button>
                  </Box>
                )}

                {!hasDataUploaded && !isGenerationStuck && !isGeneratingFile && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    No assessment data available. Upload deal assessment data to generate the file.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Grid>

          {/* Right Column - Contact and Description */}
          <Grid item xs={12} md={8} order={{ md: 2 }}>
            <Stack spacing={1}>
              {/* Contact Information */}
              <Box>
                {contactsLoading ? (
                  <Box p={1} textAlign="center">
                    <CircularProgress size={16} />
                  </Box>
                ) : contactsError ? (
                  <Typography variant="caption" color="error">
                    Error loading contacts
                  </Typography>
                ) : contactsData?.companyContacts && contactsData.companyContacts.length > 0 ? (
                  <>
                    {/* Compact single-row contact display */}
                    {!contactsExpanded && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        p={1}
                        borderRadius={1}
                        border={1}
                        borderColor={isDark ? 'grey.700' : 'grey.100'}
                        sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                          <ContactIcon color="primary" sx={{ fontSize: 16 }} />
                          {/* Contact enrichment menu */}
                          {enrichmentOp && enrichmentOp.status !== 'completed' && enrichmentOp.status !== 'failed' ? (
                            <Tooltip
                              title={`${enrichmentOp.progress?.stage || 'Processing'} (${enrichmentOp.progress?.percent || 0}%)`}
                            >
                              <CircularProgress
                                variant="determinate"
                                value={enrichmentOp.progress?.percent || 0}
                                size={14}
                              />
                            </Tooltip>
                          ) : contactEnrichmentJob ? (
                            <Tooltip title={`${contactEnrichmentJob.message} (${contactEnrichmentJob.progress}%)`}>
                              <CircularProgress variant="determinate" value={contactEnrichmentJob.progress} size={14} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Contact enrichment options">
                              <IconButton
                                size="small"
                                onClick={e => setContactMenuAnchorEl(e.currentTarget)}
                                sx={{ p: 0.2 }}
                              >
                                <MoreIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Menu
                            anchorEl={contactMenuAnchorEl}
                            open={Boolean(contactMenuAnchorEl)}
                            onClose={() => setContactMenuAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          >
                            <MenuItem
                              onClick={handleContactEnrichment}
                              disabled={!company?.dbId || Boolean(contactEnrichmentJob)}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <RefreshIcon fontSize="small" />
                                <Typography variant="body2">Enrich contacts</Typography>
                              </Box>
                            </MenuItem>
                          </Menu>
                        </Box>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {contactsData.companyContacts[0].name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ·
                        </Typography>
                        <Tooltip
                          title={
                            company?.contactEnrichmentLog && company.contactEnrichmentLog.length > 0 ? (
                              <Box sx={{ maxWidth: 360 }}>
                                <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                                  Enrichment History
                                </Typography>
                                {formatEnrichmentLog(company.contactEnrichmentLog).map((line, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    display="block"
                                    sx={{ opacity: i === 0 ? 1 : 0.8 }}
                                  >
                                    {line}
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              'No enrichment history'
                            )
                          }
                          arrow
                          placement="bottom"
                        >
                          <Box component="span">
                            {contactsData.companyContacts[0].email ? (
                              <Typography
                                component="a"
                                href={`mailto:${contactsData.companyContacts[0].email}`}
                                variant="body2"
                                noWrap
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {contactsData.companyContacts[0].email}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled" sx={{ cursor: 'help' }}>
                                —
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          ·
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {contactsData.companyContacts[0].contactType}
                        </Typography>
                        {contactsData.companyContacts.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => setContactsExpanded(true)}
                            sx={{ ml: 'auto', p: 0.2 }}
                          >
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    )}

                    {/* Full table when expanded - flows naturally in document */}
                    <Collapse in={contactsExpanded}>
                      <TableContainer
                        component={Paper}
                        sx={{
                          boxShadow: isDark ? 'none' : 2,
                          maxHeight: 400,
                          overflow: 'auto',
                          mt: 1,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          p={1}
                          borderBottom={1}
                          borderColor={isDark ? 'grey.700' : 'grey.200'}
                        >
                          <IconButton size="small" onClick={() => setContactsExpanded(false)}>
                            <ExpandLessIcon />
                          </IconButton>
                        </Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography variant="caption" fontWeight="bold">
                                  Contact Name
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" fontWeight="bold">
                                  Contact Email
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" fontWeight="bold">
                                  Contact Type
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contactsData.companyContacts.map((contact, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2">{contact.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  {contact.email ? (
                                    <Typography
                                      component="a"
                                      href={`mailto:${contact.email}`}
                                      variant="body2"
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {contact.email}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{contact.contactType}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </>
                ) : (
                  <Box p={2}>
                    <Typography variant="body2" color="text.disabled">
                      No contacts available
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Company Description */}
              <Box>
                {isEditingDescription ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <TextField
                      multiline
                      rows={4}
                      value={descriptionValue}
                      onChange={e => setDescriptionValue(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Enter company description..."
                    />
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelEdit}
                        startIcon={<CloseIcon />}
                        disabled={editLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveDescription}
                        startIcon={editLoading ? <CircularProgress size={14} /> : <SaveIcon />}
                        disabled={editLoading}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    p={2}
                    borderRadius={1}
                    border={1}
                    borderColor={isDark ? 'grey.700' : 'grey.100'}
                    maxHeight={200}
                    overflow="auto"
                    sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', position: 'relative' }}
                  >
                    <Tooltip title="Edit description">
                      <IconButton
                        size="small"
                        onClick={handleEditDescription}
                        sx={{ position: 'absolute', top: 4, right: 4, p: 0.3 }}
                      >
                        <MoreIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {decodeHtmlEntities(company?.linkedinDescription) || 'No description available'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Grid>

          {/* Key Topics and Data Timestamps */}
          <Grid item xs={12} md={8} order={{ md: 4 }}>
            {keywords.length > 0 && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LabelIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="medium">
                    Key Topics
                  </Typography>
                  {isLegacyKeywords && (
                    <Tooltip title="Using legacy XOVI keywords — core keywords not yet synthesized for this company">
                      <Chip
                        label="XOVI"
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ fontSize: '0.6rem', height: 18 }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Edit keywords">
                    <IconButton
                      size="small"
                      onClick={() => onEdit?.(ValidationSteps.VALIDATE_KEYWORDS)}
                      sx={{ p: 0.3 }}
                    >
                      <MoreIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(keywordsExpanded ? keywords : keywords.slice(0, 6)).map((kw, index) => {
                    const hasStats = kw.cpc != null || kw.searchVolume != null || kw.competitionIndex != null;
                    const chip = (
                      <Chip
                        key={index}
                        icon={<TagIcon sx={{ fontSize: '14px !important', opacity: 0.5 }} />}
                        label={kw.keyword}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 28 }}
                      />
                    );
                    if (!hasStats) return chip;
                    return (
                      <Tooltip
                        key={index}
                        arrow
                        title={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.25 }}>
                            {kw.cpc != null && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                CPC: €{kw.cpc.toFixed(2)}
                              </Typography>
                            )}
                            {kw.searchVolume != null && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                Search Vol: {kw.searchVolume.toLocaleString()}
                              </Typography>
                            )}
                            {kw.competitionIndex != null && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                Competition: {(kw.competitionIndex * 100).toFixed(0)}%
                              </Typography>
                            )}
                            {kw.keywordType && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                Type: {kw.keywordType}
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        {chip}
                      </Tooltip>
                    );
                  })}
                  {keywords.length > 6 && !keywordsExpanded && (
                    <Chip
                      key="more-keywords"
                      label={`+${keywords.length - 6} more`}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontSize: '0.75rem',
                        height: 28,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isDark ? '#4E6694' : '#3F5D66',
                          color: 'white',
                        },
                      }}
                      onClick={() => setKeywordsExpanded(true)}
                    />
                  )}
                  {keywordsExpanded && (
                    <Chip
                      key="show-less"
                      label="Show less"
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontSize: '0.75rem',
                        height: 28,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isDark ? '#4E6694' : '#3F5D66',
                          color: 'white',
                        },
                      }}
                      onClick={() => setKeywordsExpanded(false)}
                    />
                  )}
                </Box>
              </Box>
            )}
            {/* Data timestamps - right aligned */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={0.5}>
              {kpiStatusData?.kpiCalculationStatus?.lastDataUpload && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.65rem' }}>
                  Uploaded: {format(new Date(kpiStatusData.kpiCalculationStatus.lastDataUpload), 'dd MMM yyyy HH:mm')}
                </Typography>
              )}
              {generationTime && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.65rem' }}>
                  Assessment: {generationTime}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      {/* KPI Status Modal */}
      {company?.dbId && (
        <KPIStatusModal
          open={kpiModalOpen}
          onClose={() => setKpiModalOpen(false)}
          companyId={company.dbId}
          companyName={companyName}
        />
      )}

      {/* Move Company Dialog */}
      {company?.dbId && (
        <MoveCompanyDialog
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          companyId={company.dbId}
          companyName={companyName}
        />
      )}

      {/* Financial Data Modal */}
      {company?.dbId && (
        <FinancialDataModal
          open={financialDataModalOpen}
          onClose={() => setFinancialDataModalOpen(false)}
          companyId={companyIdNumber}
          companyName={companyName}
        />
      )}
    </Card>
  );
};

export default CompanyOverview;
